import type { LearningExercise, SourceRef } from "../types";
import { makeCloze, makeMulti, makeOrdering, makeSingle } from "./legalExerciseFactory";

const sources: Record<string, SourceRef> = {
  eqeHub: { id: "eqeHub", label: "EPO EQE Hub", url: "https://www.epo.org/en/learning/eqe-epac/european-qualifying-examination-eqe" },
  art52epc: { id: "art52epc", label: "Art. 52 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a52.html" },
  art53epc: { id: "art53epc", label: "Art. 53 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a53.html" },
  art54epc: { id: "art54epc", label: "Art. 54 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a54.html" },
  art55epc: { id: "art55epc", label: "Art. 55 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a55.html" },
  art76epc: { id: "art76epc", label: "Art. 76 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a76.html" },
  art83epc: { id: "art83epc", label: "Art. 83 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a83.html" },
  art84epc: { id: "art84epc", label: "Art. 84 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a84.html" },
  art87epc: { id: "art87epc", label: "Art. 87 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a87.html" },
  art88epc: { id: "art88epc", label: "Art. 88 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a88.html" },
  art90epc: { id: "art90epc", label: "Art. 90 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a90.html" },
  art92epc: { id: "art92epc", label: "Art. 92 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a92.html" },
  art94epc: { id: "art94epc", label: "Art. 94 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a94.html" },
  art97epc: { id: "art97epc", label: "Art. 97 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a97.html" },
  art123epc: { id: "art123epc", label: "Art. 123 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a123.html" },
  r40epc: { id: "r40epc", label: "R. 40 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/r40.html" },
  r42epc: { id: "r42epc", label: "R. 42 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/r42.html" },
  r43epc: { id: "r43epc", label: "R. 43 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/r43.html" },
  r159epc: { id: "r159epc", label: "R. 159 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/r159.html" },
  pct11: { id: "pct11", label: "Art. 11 PCT", url: "https://www.wipo.int/pct/en/texts/articles/a11.html" },
  pct19: { id: "pct19", label: "Art. 19 PCT", url: "https://www.wipo.int/pct/en/texts/articles/a19.html" },
  pct22: { id: "pct22", label: "Art. 22 PCT", url: "https://www.wipo.int/pct/en/texts/articles/a22.html" },
  pct31: { id: "pct31", label: "Art. 31 PCT", url: "https://www.wipo.int/pct/en/texts/articles/a31.html" },
  pct34: { id: "pct34", label: "Art. 34 PCT", url: "https://www.wipo.int/pct/en/texts/articles/a34.html" },
  pct39: { id: "pct39", label: "Art. 39 PCT", url: "https://www.wipo.int/pct/en/texts/articles/a39.html" },
};

const pathId = "epu-eqe" as const;

export const eqeVerfahrenPctDrafting: LearningExercise[] = [
  makeSingle(sources, {
    id: "eqe-vpd-001",
    pathId,
    reviewStatus: "beta-ready",
    topic: "EQE Reform - Module",
    title: "Neue EQE: F und M1-M4",
    prompt: "Welche Aussage beschreibt die reformierte EQE-Struktur ab 2027 zutreffend?",
    options: [
      "Paper F bildet das Foundation-Modul; die Main Examination besteht aus M1 bis M4.",
      "Die EQE besteht nur noch aus Paper D.",
      "M3 und M4 wurden 2026 letztmalig angeboten.",
      "Paper F ist ein nationales deutsches Amtsjahrmodul.",
    ],
    answer: 0,
    explanation:
      "Der EPO-EQE-Hub und die verifizierte EQE-Schaerfung im Research-Memo beschreiben die modulare Struktur mit Paper F und M1 bis M4. Paper F startete im Maerz 2025, M1/M2 ab 2026 und M3/M4 ab 2027; 2026 war damit noch Uebergang mit M1/M2 und den letzten Legacy-Papers.",
    sourceIds: ["eqeHub"],
    levelNumber: 1,
    errorCategory: "Pruefungsstruktur",
    skillTags: ["eqe", "syllabus"],
  }),
  makeMulti(sources, {
    id: "eqe-vpd-002",
    pathId,
    reviewStatus: "beta-ready",
    topic: "EPÜ - Ausschluesse",
    title: "Art. 52(2)/(3): Was ist nicht als solches patentfaehig?",
    prompt: "Welche Beispiele gehoeren typischerweise zu den Ausschluessen nach Art. 52(2) EPUE, soweit sie als solche beansprucht werden?",
    options: [
      "Mathematische Methoden.",
      "Programme fuer Datenverarbeitungsanlagen als solche.",
      "Wiedergabe von Informationen als solche.",
      "Eine neue technische Vorrichtung mit strukturellen Merkmalen.",
    ],
    answers: [0, 1, 2],
    explanation:
      "Art. 52(2) EPUE nennt u.a. mathematische Methoden, Programme fuer Datenverarbeitungsanlagen und die Wiedergabe von Informationen. Art. 52(3) begrenzt den Ausschluss auf den Gegenstand als solchen. Eine technische Vorrichtung ist daher nicht allein wegen Software-/Informationsbezug ausgeschlossen.",
    sourceIds: ["art52epc"],
    levelNumber: 2,
    errorCategory: "Ausschluss",
    skillTags: ["eqe", "patentierbarkeit", "art52"],
  }),
  makeMulti(sources, {
    id: "eqe-vpd-003",
    pathId,
    reviewStatus: "beta-ready",
    topic: "EPÜ - Art. 53 / Medical use",
    title: "Medizinische Verfahren und medizinische Verwendung",
    prompt: "Welche Aussagen zu Art. 53 EPUE und medizinischer Verwendung treffen zu?",
    options: [
      "Therapeutische, chirurgische und diagnostische Verfahren am menschlichen oder tierischen Koerper sind nach Art. 53(c) EPUE ausgeschlossen.",
      "Art. 53(c) EPUE schliesst Erzeugnisse, insbesondere Stoffe oder Stoffgemische, zur Anwendung in solchen Verfahren nicht als solche aus.",
      "Art. 54(4)/(5) EPUE ermoeglicht medizinische Verwendungsansprueche unter den gesetzlichen Voraussetzungen.",
      "Jede Arzneimittelerfindung ist im EPUE generell ausgeschlossen.",
    ],
    answers: [0, 1, 2],
    explanation:
      "Art. 53(c) EPUE betrifft bestimmte Verfahren am menschlichen oder tierischen Koerper, nimmt aber Erzeugnisse fuer solche Verfahren aus dem Ausschluss heraus. Art. 54(4)/(5) EPUE ist der zentrale Anker fuer erste bzw. weitere medizinische Verwendung.",
    sourceIds: ["art53epc", "art54epc"],
    levelNumber: 3,
    errorCategory: "Abgrenzung",
    skillTags: ["eqe", "medical-use", "art53"],
  }),
  makeSingle(sources, {
    id: "eqe-vpd-004",
    pathId,
    reviewStatus: "beta-ready",
    topic: "EPÜ - Art. 83",
    title: "Ausfuehrbarkeit",
    prompt: "Was verlangt Art. 83 EPUE im Kern?",
    options: [
      "Die Anmeldung muss die Erfindung so deutlich und vollstaendig offenbaren, dass ein Fachmann sie ausfuehren kann.",
      "Die Ansprueche muessen immer genau ein Merkmal enthalten.",
      "Der Anmelder muss Markterfolg nachweisen.",
      "Der Recherchenbericht muss bereits positiv sein.",
    ],
    answer: 0,
    explanation:
      "Art. 83 EPUE verlangt eine deutliche und vollstaendige Offenbarung fuer den Fachmann. In EQE-Faellen ist die Abgrenzung zu Art. 84 wichtig: Art. 83 betrifft Ausfuehrbarkeit, Art. 84 Klarheit und Stuetzung der Ansprueche.",
    sourceIds: ["art83epc", "art84epc"],
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["eqe", "art83", "offenbarung"],
  }),
  makeSingle(sources, {
    id: "eqe-vpd-005",
    pathId,
    reviewStatus: "beta-ready",
    topic: "EPÜ - Art. 84",
    title: "Klarheit und Stuetzung",
    prompt: "Welche Aussage trifft Art. 84 EPUE am besten?",
    options: [
      "Die Patentansprueche muessen deutlich und knapp gefasst sein und von der Beschreibung gestuetzt werden.",
      "Art. 84 EPUE regelt nur die Einspruchsfrist.",
      "Art. 84 EPUE erlaubt nach Erteilung jede Schutzbereichserweiterung.",
      "Art. 84 EPUE ersetzt die Neuheitspruefung.",
    ],
    answer: 0,
    explanation:
      "Art. 84 EPUE ist der Klarheitsanker: Ansprueche muessen deutlich, knapp und von der Beschreibung gestuetzt sein. In der EQE ist das besonders fuer Claim-Analyse und Aenderungs-/Verteidigungsfragen relevant.",
    sourceIds: ["art84epc"],
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["eqe", "art84", "claims"],
  }),
  makeSingle(sources, {
    id: "eqe-vpd-006",
    pathId,
    reviewStatus: "beta-ready",
    topic: "EPÜ - Art. 55",
    title: "Unschädliche Offenbarung",
    prompt: "Welche Aussage zu Art. 55 EPUE ist richtig?",
    options: [
      "Bestimmte Offenbarungen bleiben nur unter engen Voraussetzungen und innerhalb der sechs Monate vor Einreichung ausser Betracht.",
      "Jede Eigenveroeffentlichung ist fuer 12 Monate unschaedlich.",
      "Art. 55 EPUE gilt nur fuer Marken.",
      "Art. 55 EPUE ersetzt das Prioritaetsrecht.",
    ],
    answer: 0,
    explanation:
      "Art. 55 EPUE regelt unschaedliche Offenbarungen eng, insbesondere bei offensichtlichem Missbrauch oder bestimmten Ausstellungen, und knuepft an eine Sechsmonatsfrist an. Das ist nicht mit einer allgemeinen Grace Period zu verwechseln.",
    sourceIds: ["art55epc"],
    levelNumber: 2,
    errorCategory: "Ausnahme",
    skillTags: ["eqe", "neuheit", "art55"],
  }),
  makeSingle(sources, {
    id: "eqe-vpd-007",
    pathId,
    reviewStatus: "beta-ready",
    topic: "EPÜ - Teilanmeldung",
    title: "Art. 76 EPUE: Offenbarungsgrenze",
    prompt: "Welche Grenze setzt Art. 76 EPUE fuer europaeische Teilanmeldungen?",
    options: [
      "Die Teilanmeldung darf nicht ueber den Inhalt der frueheren Anmeldung in der urspruenglich eingereichten Fassung hinausgehen.",
      "Die Teilanmeldung darf immer breiter sein als jede Stammanmeldung.",
      "Teilanmeldungen sind im EPUE ausgeschlossen.",
      "Eine Teilanmeldung braucht nie Ansprueche.",
    ],
    answer: 0,
    explanation:
      "Art. 76 EPUE ist die zentrale Offenbarungsgrenze fuer Teilanmeldungen. Die Logik entspricht dem strengen Offenbarungsdenken des Art. 123(2) EPUE: Kein neuer Gegenstand gegenueber der urspruenglichen Offenbarung.",
    sourceIds: ["art76epc", "art123epc"],
    levelNumber: 2,
    errorCategory: "Aenderung",
    skillTags: ["eqe", "teilanmeldung", "added-matter"],
  }),
  makeMulti(sources, {
    id: "eqe-vpd-008",
    pathId,
    reviewStatus: "beta-ready",
    topic: "EPÜ - Prioritaet",
    title: "Mehrfach- und Teilprioritaet",
    prompt: "Welche Aussagen zur Prioritaet nach Art. 87/88 EPUE sind richtig?",
    options: [
      "Art. 87 EPUE verankert das Prioritaetsrecht fuer dieselbe Erfindung.",
      "Art. 88 EPUE laesst unter Voraussetzungen Mehrfachprioritaeten zu.",
      "Prioritaet ersetzt jede Neuheitspruefung.",
      "Prioritaet ist nur im Markenrecht bekannt.",
    ],
    answers: [0, 1],
    explanation:
      "Art. 87 EPUE regelt den Grundsatz des Prioritaetsrechts; Art. 88 EPUE behandelt u.a. Mehrfachprioritaeten. Prioritaet verschiebt den relevanten Zeitrang fuer den beanspruchten Gegenstand, ersetzt aber nicht die materielle Neuheits- und Erfindungshoehenpruefung.",
    sourceIds: ["art87epc", "art88epc"],
    levelNumber: 2,
    errorCategory: "Prioritaet",
    skillTags: ["eqe", "prioritaet"],
  }),
  makeOrdering(sources, {
    id: "eqe-vpd-009",
    pathId,
    reviewStatus: "beta-ready",
    topic: "EPÜ - Erteilungsverfahren",
    title: "Ablauf der europaeischen Anmeldung",
    prompt: "Ordnen Sie den groben Ablauf des EPUE-Erteilungsverfahrens.",
    orderItems: [
      "Einreichung und Zuerkennung eines Anmeldetags nach Art. 80/R. 40 EPUE.",
      "Eingangs- und Formalpruefung nach Art. 90 EPUE.",
      "Europaeische Recherche und Recherchenbericht nach Art. 92 EPUE.",
      "Sachpruefung nach Pruefungsantrag, Art. 94 EPUE.",
      "Erteilung oder Zurueckweisung, Art. 97 EPUE.",
    ],
    explanation:
      "Das EPUE-Verfahren folgt einem klaren Bogen: Anmeldetag, Formalpruefung, Recherche, Sachpruefung und Erteilung/Zurueckweisung. Diese Reihenfolge ist fuer Paper F/M2 und fuer Fristenfragen besonders wichtig.",
    sourceIds: ["r40epc", "art90epc", "art92epc", "art94epc", "art97epc"],
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["eqe", "erteilungsverfahren", "ordering"],
  }),
  makeSingle(sources, {
    id: "eqe-vpd-010",
    pathId,
    reviewStatus: "beta-ready",
    topic: "EPÜ - Anmeldetag",
    title: "Mindestanforderungen fuer den Anmeldetag",
    prompt: "Welche Funktion haben Art. 80 und R. 40 EPUE?",
    options: [
      "Sie definieren die Voraussetzungen, unter denen eine europaeische Anmeldung einen Anmeldetag erhaelt.",
      "Sie regeln ausschliesslich die Einspruchsgruende.",
      "Sie bestimmen die maximale Patentlaufzeit.",
      "Sie ersetzen die Beschreibung durch einen Markennamen.",
    ],
    answer: 0,
    explanation:
      "Art. 80 EPUE verweist fuer den Anmeldetag auf die Ausfuehrungsordnung; R. 40 EPUE nennt die Mindestbestandteile. Der Anmeldetag ist fuer Prioritaet, Stand der Technik und Fristen der erste harte Verfahrensanker.",
    sourceIds: ["r40epc"],
    levelNumber: 1,
    errorCategory: "Verfahren",
    skillTags: ["eqe", "anmeldetag"],
  }),
  makeSingle(sources, {
    id: "eqe-vpd-011",
    pathId,
    reviewStatus: "beta-ready",
    topic: "EPÜ - Recherche",
    title: "Europaeischer Recherchenbericht",
    prompt: "Welche Rolle spielt Art. 92 EPUE?",
    options: [
      "Er verankert die Erstellung des europaeischen Recherchenberichts.",
      "Er regelt die nationale Phase des PCT.",
      "Er schafft die Zwangslizenz.",
      "Er definiert die Verschwiegenheitspflicht.",
    ],
    answer: 0,
    explanation:
      "Art. 92 EPUE betrifft die europaeische Recherche. Fuer EQE-Aufgaben ist die Recherche der Uebergang von formaler Anmeldung zu materieller Bewertung gegen den Stand der Technik.",
    sourceIds: ["art92epc"],
    levelNumber: 1,
    errorCategory: "Verfahren",
    skillTags: ["eqe", "recherche"],
  }),
  makeSingle(sources, {
    id: "eqe-vpd-012",
    pathId,
    reviewStatus: "beta-ready",
    topic: "PCT - Internationale Phase",
    title: "Internationaler Anmeldetag",
    prompt: "Welche Aussage zu Art. 11 PCT ist richtig?",
    options: [
      "Art. 11 PCT regelt Voraussetzungen fuer die Zuerkennung eines internationalen Anmeldetags.",
      "Art. 11 PCT regelt nur Markenwidersprueche.",
      "Art. 11 PCT erlaubt keine Prioritaeten.",
      "Art. 11 PCT ist nur nach Patenterteilung relevant.",
    ],
    answer: 0,
    explanation:
      "Art. 11 PCT ist der Anker fuer den internationalen Anmeldetag. Fuer PCT-Faelle ist das vergleichbar wichtig wie der Anmeldetag im EPUE-System: Er beeinflusst Zeitrang, internationale Phase und spaetere Eintrittsfristen.",
    sourceIds: ["pct11"],
    levelNumber: 1,
    errorCategory: "Verfahren",
    skillTags: ["eqe", "pct", "anmeldetag"],
  }),
  makeCloze(sources, {
    id: "eqe-vpd-013",
    pathId,
    reviewStatus: "beta-ready",
    topic: "PCT - Nationale Phase",
    title: "PCT Art. 22: Grundfrist",
    prompt: "Art. 22 PCT setzt fuer viele nationale/regionale Eintrittshandlungen eine Grundfrist von ___ Monaten ab Prioritaetsdatum.",
    options: ["18", "20", "30", "31"],
    answer: 2,
    explanation:
      "Art. 22 PCT arbeitet mit einer 30-Monats-Grundfrist. Fuer den Eintritt vor dem EPA als Euro-PCT ist daneben R. 159 EPUE mit 31 Monaten der zentrale europaeische Spezialanker.",
    sourceIds: ["pct22", "r159epc"],
    levelNumber: 1,
    errorCategory: "Frist",
    skillTags: ["eqe", "pct", "fristen"],
  }),
  makeSingle(sources, {
    id: "eqe-vpd-014",
    pathId,
    reviewStatus: "beta-ready",
    topic: "PCT - Art. 19 / Art. 34",
    title: "Aenderungen in der internationalen Phase",
    prompt: "Welche Abgrenzung zwischen Art. 19 und Art. 34 PCT ist richtig?",
    options: [
      "Art. 19 PCT betrifft Aenderungen der Ansprueche nach dem internationalen Recherchenbericht; Art. 34 PCT betrifft Aenderungen im Rahmen der internationalen vorlaeufigen Pruefung.",
      "Art. 19 PCT betrifft nur Marken; Art. 34 PCT nur Designs.",
      "Art. 19 PCT erlaubt Aenderungen der Beschreibung, Art. 34 PCT verbietet jede Aenderung.",
      "Beide Normen betreffen ausschliesslich nationale Gerichtsverfahren.",
    ],
    answer: 0,
    explanation:
      "Art. 19 PCT erlaubt Anspruchsaenderungen nach Erhalt des internationalen Recherchenberichts. Art. 34 PCT gehoert zu Kapitel II und erlaubt Aenderungen im Rahmen der internationalen vorlaeufigen Pruefung.",
    sourceIds: ["pct19", "pct34"],
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["eqe", "pct", "aenderung"],
  }),
  makeOrdering(sources, {
    id: "eqe-vpd-015",
    pathId,
    reviewStatus: "beta-ready",
    topic: "PCT / Euro-PCT",
    title: "PCT-Phasen",
    prompt: "Ordnen Sie den groben PCT-Verfahrensablauf bis zum EPA-Eintritt.",
    orderItems: [
      "Internationale Anmeldung mit internationalem Anmeldetag.",
      "Internationale Recherche und ggf. Art.-19-PCT-Anspruchsaenderung.",
      "Optional Kapitel-II-Antrag auf internationale vorlaeufige Pruefung mit Art.-34-PCT-Aenderungen.",
      "Eintritt in nationale/regionale Phase; vor dem EPA nach R. 159 EPUE grundsaetzlich 31 Monate.",
    ],
    explanation:
      "Der PCT trennt internationale Phase und nationale/regionale Phase. Art. 19 und Art. 34 PCT sind unterschiedliche Aenderungsfenster; R. 159 EPUE ist der zentrale Euro-PCT-Eintrittsanker.",
    sourceIds: ["pct11", "pct19", "pct34", "r159epc"],
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["eqe", "pct", "ordering"],
  }),
  makeSingle(sources, {
    id: "eqe-vpd-016",
    pathId,
    reviewStatus: "beta-ready",
    topic: "Anspruchslogik",
    title: "Zweiteilige Anspruchsform",
    prompt: "Welche Aussage zur zweiteiligen Anspruchsform nach R. 43 EPUE ist richtig?",
    options: [
      "Sie trennt geeigneterweise bekannte Merkmale im Oberbegriff von kennzeichnenden Merkmalen, soweit dies zweckmaessig ist.",
      "Sie ist in jedem Fall verboten.",
      "Sie ersetzt die Beschreibung vollstaendig.",
      "Sie gilt nur fuer Markenklassen.",
    ],
    answer: 0,
    explanation:
      "R. 43 EPUE regelt Anspruchsform und Anspruchsinhalt. Die zweiteilige Form ist ein klassischer Drafting-Anker: Oberbegriff fuer bekannte Merkmale, kennzeichnender Teil fuer die unterscheidenden technischen Merkmale, sofern zweckmaessig.",
    sourceIds: ["r43epc"],
    levelNumber: 2,
    errorCategory: "Drafting",
    skillTags: ["eqe", "drafting", "claims"],
  }),
  makeOrdering(sources, {
    id: "eqe-vpd-017",
    pathId,
    reviewStatus: "beta-ready",
    topic: "Anspruchslogik",
    title: "Merkmalsanalyse fuer Neuheit",
    prompt: "Ordnen Sie die Schritte einer sauberen Neuheitsanalyse.",
    orderItems: [
      "Anspruch in technische Merkmale gliedern.",
      "Ein einzelnes Stand-der-Technik-Dokument auswaehlen.",
      "Fuer jedes Merkmal pruefen, ob es unmittelbar und eindeutig offenbart ist.",
      "Neuheit nur verneinen, wenn alle Merkmale in diesem einen Dokument offenbart sind.",
    ],
    explanation:
      "Art. 54 EPUE verlangt eine Einzelvergleichslogik: Fuer Neuheit darf nicht aus mehreren Dokumenten mosaikartig kombiniert werden. Die Merkmalsgliederung ist die Bruecke zwischen Claim-Drafting und materieller Pruefung.",
    sourceIds: ["art54epc"],
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["eqe", "neuheit", "claim-analysis"],
  }),
  makeOrdering(sources, {
    id: "eqe-vpd-018",
    pathId,
    reviewStatus: "beta-ready",
    topic: "Anspruchslogik",
    title: "Fallback-Hierarchie",
    prompt: "Ordnen Sie eine sinnvolle Fallback-Logik fuer Anspruchsaenderungen.",
    orderItems: [
      "Technischen Kern und breitesten noch vertretbaren Anspruch bestimmen.",
      "Urspruengliche Offenbarung fuer jedes moegliche Fallback-Merkmal pruefen.",
      "Fallbacks nach technischer Wirkung und Verteidigungswert staffeln.",
      "Aenderung gegen Art. 123(2), Art. 84 und ggf. Art. 123(3) EPUE pruefen.",
    ],
    explanation:
      "Anspruchsaenderungen sind nur brauchbar, wenn sie offenbart, klar und strategisch sinnvoll sind. Art. 123(2) verhindert neuen Gegenstand; Art. 84 fordert klare Ansprueche; Art. 123(3) begrenzt nach Erteilung die Schutzbereichserweiterung.",
    sourceIds: ["art123epc", "art84epc"],
    levelNumber: 3,
    errorCategory: "Drafting",
    skillTags: ["eqe", "drafting", "added-matter"],
  }),
  makeSingle(sources, {
    id: "eqe-vpd-019",
    pathId,
    reviewStatus: "beta-ready",
    topic: "Beschreibung und Ansprueche",
    title: "R. 42 EPUE: Beschreibung",
    prompt: "Welche Funktion hat R. 42 EPUE fuer die Beschreibung einer europaeischen Patentanmeldung?",
    options: [
      "Sie strukturiert, welche Angaben die Beschreibung enthalten soll, etwa technisches Gebiet, Hintergrund, Erfindung und Ausfuehrungsbeispiele.",
      "Sie regelt ausschliesslich die Beschwerdefrist.",
      "Sie ersetzt die Patentansprueche.",
      "Sie verbietet Ausfuehrungsbeispiele.",
    ],
    answer: 0,
    explanation:
      "R. 42 EPUE strukturiert den Inhalt der Beschreibung. Zusammen mit R. 43 EPUE fuer Ansprueche bildet sie die Grundlage fuer Drafting- und Analyseaufgaben in M1/M3.",
    sourceIds: ["r42epc", "r43epc"],
    levelNumber: 1,
    errorCategory: "Drafting",
    skillTags: ["eqe", "description", "drafting"],
  }),
  makeSingle(sources, {
    id: "eqe-vpd-020",
    pathId,
    reviewStatus: "beta-ready",
    topic: "Inescapable Trap",
    title: "Art. 123(2)/(3)-Falle",
    prompt: "Was ist die Grundidee der Art.-123(2)/(3)-Falle?",
    options: [
      "Ein unzulaessig hinzugefuegtes beschraenkendes Merkmal kann nach Erteilung problematisch sein, wenn seine Streichung den Schutzbereich erweitern wuerde.",
      "Jede Aenderung vor Erteilung ist automatisch verboten.",
      "Art. 123 betrifft nur Prioritaetsfristen.",
      "Art. 123 erlaubt nach Erteilung immer breitere Ansprueche.",
    ],
    answer: 0,
    explanation:
      "Art. 123(2) EPUE verbietet neuen Gegenstand; Art. 123(3) EPUE verbietet nach Erteilung Schutzbereichserweiterungen. Die Falle entsteht, wenn ein hinzugefuegtes Merkmal gegen Art. 123(2) verstoesst, aber seine Entfernung den Schutzbereich erweitert.",
    sourceIds: ["art123epc"],
    levelNumber: 3,
    errorCategory: "Aenderung",
    skillTags: ["eqe", "added-matter", "art123"],
  }),
];
